import React, { useId, useState } from 'react';

interface TagInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
  label?: string;
  error?: string;
  // One-click tags shown under the input; a suggestion already applied is hidden.
  suggestions?: { label: string; hint?: string }[];
}

const TagInput: React.FC<TagInputProps> = ({ tags, onChange, placeholder = 'Type and press Enter or click +', label, error, suggestions = [] }) => {
  const [input, setInput] = useState('');
  const inputId = useId();

  const addTag = () => {
    const value = input.trim();
    if (value && !tags.includes(value)) {
      onChange([...tags, value]);
    }
    setInput('');
  };

  const has = (value: string) => tags.some((t) => t.toLowerCase() === value.toLowerCase());
  const addSuggested = (value: string) => { if (!has(value)) onChange([...tags, value]); };
  const pending = suggestions.filter((sug) => !has(sug.label));

  const removeTag = (index: number) => {
    onChange(tags.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  return (
    <div>
      {label && <label htmlFor={inputId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>}
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          aria-invalid={!!error || undefined}
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          className={`block flex-1 rounded-md shadow-sm focus:border-emerald-500 focus:ring-emerald-500 ${error ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'}`}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={addTag}
          disabled={!input.trim()}
          className="px-3 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          +
        </button>
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {pending.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mt-2">
          <span className="text-xs text-gray-500 dark:text-gray-400">Suggested:</span>
          {pending.map((sug) => (
            <button
              key={sug.label}
              type="button"
              onClick={() => addSuggested(sug.label)}
              title={sug.hint ? `${sug.hint}. Click to add.` : 'Click to add'}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm border border-dashed border-gray-400 dark:border-gray-500 text-gray-700 dark:text-gray-300 hover:border-emerald-500 hover:text-emerald-700 dark:hover:text-emerald-400"
            >
              <span aria-hidden="true">+</span> {sug.label}
            </button>
          ))}
        </div>
      )}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-emerald-100 text-emerald-800"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(index)}
                className="ml-1 text-emerald-600 hover:text-emerald-900"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default TagInput;
